/**
 * <athena-search> — in-nav pill search with a suggestion pop-over.
 *
 * Markup lives in sections/athena-header.liquid; the product result tiles are
 * rendered server side by sections/athena-predictive-search.liquid and fetched
 * from Shopify's predictive search endpoint.
 *
 * The panel has a single layout and the aside never changes. Typing only moves
 * the main column: the popular tiles give way to skeleton tiles the instant a
 * query starts, and those are replaced by the real ones when they land — so the
 * column always has tiles in it and the panel never shifts. data-state and
 * data-showing are reflected on the host and CSS does the swap:
 *   empty   — focused, query blank: trending searches + popular products
 *   typing  — query non-blank with at least one match
 *   none    — query non-blank with zero matches
 */
(function () {
  if (!window.customElements) return;
  if (customElements.get('athena-search')) return;

  const DEBOUNCE_MS = 200;
  const OPTION_SELECTOR = '[data-athena-search-option]';

  class AthenaSearch extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.debounceTimer = null;
      this.cache = new Map();
      this.activeIndex = -1;
      // Set while we move focus programmatically, so restoring focus after
      // Escape does not immediately re-open the panel.
      this.suppressOpen = false;

      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this.header = this.closest('athena-header');
      this.panel = this.querySelector('[data-athena-search-panel]');
      this.forms = Array.from(this.querySelectorAll('[data-athena-search-form]'));
      this.inputs = Array.from(this.querySelectorAll('[data-athena-search-input]'));
      this.resultsSlot = this.querySelector('[data-athena-search-results]');
      this.mainLabel = this.querySelector('[data-athena-search-main-label]');
      this.queryLabels = Array.from(this.querySelectorAll('[data-athena-search-query-label]'));
      this.liveRegion = this.querySelector('[data-athena-search-live]');
      this.mobileToggle = this.querySelector('[data-athena-search-mobile-toggle]');
      this.clearButtons = Array.from(this.querySelectorAll('[data-athena-search-clear]'));
      this.catcher = this.querySelector('[data-athena-search-catcher]');

      // Tiles are rendered by the shared athena-header-nav-card snippet, which
      // cannot carry our option attribute — stamp it on here so arrow-key
      // navigation still reaches them.
      this.markTilesAsOptions(this);

      this.sectionId = this.dataset.sectionId || 'athena-predictive-search';
      this.productLimit = parseInt(this.dataset.productLimit, 10) || 4;
      this.searchFields = (this.dataset.searchFields || '').trim();

      this.bindEvents();
      this.setQuery(this.currentQuery(), { silent: true });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocumentKeydown);
      clearTimeout(this.debounceTimer);
      if (this.abortController) this.abortController.abort();
    }

    /* ---------------------------------------------------------------- setup */

    bindEvents() {
      this.inputs.forEach((input) => {
        input.addEventListener('focus', () => this.open());
        // Escape leaves focus in the input with the panel closed; a click
        // re-opens it even though no focus event fires.
        input.addEventListener('click', () => this.open());
        // Typing re-opens: the panel can be closed with focus still in the
        // input (after Escape, or after clearing with the ✕), and a keystroke
        // that produced no visible results would be a dead end.
        input.addEventListener('input', () => {
          this.open();
          this.setQuery(input.value);
        });
        input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      });

      this.clearButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.setQuery('');
          this.close();
          // Silent, so restoring the caret does not trip the focus handler and
          // re-open the panel we just closed.
          this.focusInput({ silent: true });
        });
      });

      if (this.mobileToggle) {
        this.mobileToggle.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
          this.focusInput();
        });
      }

      if (this.catcher) {
        this.catcher.addEventListener('click', () => this.close());
      }

      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => {
          if (!this.currentQuery().trim()) event.preventDefault();
        });
      });

      document.addEventListener('keydown', this.onDocumentKeydown);

      // Keep focus-out closing the panel without stealing clicks inside it.
      this.addEventListener('focusout', (event) => {
        if (!this.isOpen()) return;
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        if (!event.relatedTarget) return;
        this.close();
      });
    }

    /* ---------------------------------------------------------------- state */

    isOpen() {
      return this.dataset.open === 'true';
    }

    open() {
      if (this.suppressOpen || this.isOpen()) return;

      this.dataset.open = 'true';
      this.inputs.forEach((input) => input.setAttribute('aria-expanded', 'true'));

      if (this.header) {
        this.header.setAttribute('search-open', 'true');
        // Close any nav panel that is showing so the two never overlap.
        this.header.setAttribute('expanded-nav-open', 'false');
        this.header.setAttribute('scent-nav-open', 'false');

        // The panel is white, so the nav has to be on its light theme while the
        // panel is open. The header ignores its own scroll/hover theme switching
        // while search-open is true, and recomputes on close.
        this.header.setAttribute('nav-theme', 'light');
      }

      const query = this.currentQuery().trim();
      if (query && this.resultsSlot && !this.resultsSlot.innerHTML.trim()) {
        this.setQuery(query);
      }
    }

    close() {
      if (!this.isOpen()) return;

      this.dataset.open = 'false';
      this.inputs.forEach((input) => input.setAttribute('aria-expanded', 'false'));
      this.setActiveIndex(-1);

      if (this.header) {
        this.header.setAttribute('search-open', 'false');

        if (typeof this.header.updateThemeBasedOnScroll === 'function') {
          this.header.updateThemeBasedOnScroll();
        }
      }
    }

    currentQuery() {
      const input = this.inputs.find((candidate) => candidate.value !== '');
      return input ? input.value : '';
    }

    focusInput(options) {
      const visible = this.inputs.find((input) => input.offsetParent !== null);
      if (!visible) return;

      if (options && options.silent) {
        this.suppressOpen = true;
        visible.focus();
        this.suppressOpen = false;
        return;
      }

      visible.focus();
    }

    setQuery(value, options) {
      const silent = options && options.silent;
      const query = value == null ? '' : value;

      this.inputs.forEach((input) => {
        if (input.value !== query) input.value = query;
      });

      this.dataset.hasQuery = query.trim() ? 'true' : 'false';
      this.queryLabels.forEach((label) => {
        label.textContent = query.trim();
      });

      if (!query.trim()) {
        clearTimeout(this.debounceTimer);
        if (this.abortController) this.abortController.abort();
        if (this.resultsSlot) this.resultsSlot.innerHTML = '';
        this.setState('empty');
        return;
      }

      if (silent) return;

      // Placeholders go in before the debounce, not after it — waiting for
      // fetchSuggestions would leave the column empty for the debounce plus the
      // round trip. Later keystrokes keep the results already on screen rather
      // than flashing back to skeletons on every character.
      if (!this.hasResultTiles()) this.showSkeleton();

      this.setState('typing');

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchSuggestions(query), DEBOUNCE_MS);
    }

    /**
     * Only the host attribute moves — the stylesheet decides which blocks of the
     * single panel layout are visible for each state.
     */
    setState(state) {
      this.dataset.state = state;

      this.syncMainColumn();
      this.setActiveIndex(-1);
    }

    /** Skeleton tiles count too — they occupy the same grid as the real ones. */
    hasResultTiles() {
      return Boolean(this.resultsSlot && this.resultsSlot.querySelector('.athena-search__tiles'));
    }

    /** Placeholder tiles, shaped like the cards they stand in for. */
    showSkeleton() {
      if (!this.resultsSlot) return;

      const tile =
        '<div class="athena-search__skeleton-tile">' +
        '<div class="athena-search__skeleton-media"></div>' +
        '<div class="athena-search__skeleton-line"></div>' +
        '<div class="athena-search__skeleton-line athena-search__skeleton-line--short"></div>' +
        '</div>';

      // The tile grid caps at four columns, same as the popular set.
      const columns = Math.min(this.productLimit, 4);

      this.resultsSlot.innerHTML =
        `<div class="athena-search__tiles" aria-hidden="true" style="--as-popular-columns: ${columns};">` +
        tile.repeat(this.productLimit) +
        '</div>';
    }

    /**
     * The main column shows tiles at every point in a query — popular, then
     * skeleton, then real results — and falls back to popular when a query
     * matches nothing.
     */
    syncMainColumn() {
      const showResults = this.hasResultTiles() && this.dataset.state !== 'empty';

      this.dataset.showing = showResults ? 'results' : 'popular';

      if (!this.mainLabel) return;

      const label = this.mainLabel.dataset[showResults ? 'resultsLabel' : 'popularLabel'];
      if (label) this.mainLabel.textContent = label;

      // Nothing to head if this store has no popular collection configured.
      this.mainLabel.hidden = !showResults && !this.querySelector('[data-athena-search-popular]');
    }

    /** athena-header-nav-card cannot carry the option attribute itself. */
    markTilesAsOptions(root) {
      root.querySelectorAll('.athena-search__tiles .athena-header-nav-card').forEach((card) => {
        card.setAttribute('data-athena-search-option', '');
      });
    }

    /* ----------------------------------------------------------------- data */

    async fetchSuggestions(query) {
      const trimmed = query.trim();
      if (!trimmed) return;

      if (this.cache.has(trimmed)) {
        this.applySuggestions(trimmed, this.cache.get(trimmed));
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      // `last` keeps out-of-stock products in the results, ranked after
      // everything in stock. Set explicitly rather than left to default so the
      // dropdown shows them whatever Search & Discovery's store-wide Search
      // setting is. Draft and archived products never appear either way —
      // Shopify omits those from every buyer-facing surface.
      const params = new URLSearchParams({
        q: trimmed,
        section_id: this.sectionId,
        'resources[type]': 'product',
        'resources[limit]': String(this.productLimit),
        'resources[options][unavailable_products]': 'last',
      });

      // Narrow the match to the product and variant names. Predictive search
      // otherwise also matches body, tag, sku, barcode and vendor, which
      // surfaces products whose name has nothing to do with the query.
      if (this.searchFields) params.set('resources[options][fields]', this.searchFields);

      try {
        const response = await fetch(`/search/suggest?${params}`, {
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          // 422 means a query parameter value was rejected — most likely the
          // configured match fields. The body says which, so surface it rather
          // than failing silently to an empty dropdown.
          const detail = response.status === 422 ? await response.text() : '';
          throw new Error(`Predictive search failed: ${response.status} ${detail}`);
        }

        const markup = await response.text();
        this.cache.set(trimmed, markup);

        // A newer keystroke may have landed while this was in flight.
        if (trimmed !== this.currentQuery().trim()) return;

        this.applySuggestions(trimmed, markup);
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.warn('[Athena Search]', error.message);

        // Fail soft: the popular tiles stay in place as the fallback.
        if (this.resultsSlot) this.resultsSlot.innerHTML = '';
        this.setState('typing');
      }
    }

    applySuggestions(query, markup) {
      if (!this.resultsSlot) return;

      this.resultsSlot.innerHTML = markup;
      this.markTilesAsOptions(this.resultsSlot);

      const results = this.resultsSlot.querySelector('.athena-search__results');
      const hasRemoteResults = Boolean(results) && results.dataset.hasResults === 'true';

      // setState re-runs syncMainColumn, which now sees the new tiles.
      this.setState(hasRemoteResults ? 'typing' : 'none');
      this.announce(query, this.options().length);
    }

    announce(query, count) {
      if (!this.liveRegion) return;

      const template = this.liveRegion.dataset.template || '[count] suggestions for [query]';
      this.liveRegion.textContent = template
        .replace('[count]', String(count))
        .replace('[query]', query);
    }

    /* ------------------------------------------------------------- keyboard */

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen()) return;

      // If focus had moved into the panel, bring it back to the input.
      const focusWasInPanel = this.panel && this.panel.contains(document.activeElement);

      this.close();

      if (focusWasInPanel || !this.inputs.includes(document.activeElement)) {
        this.focusInput({ silent: true });
      }
    }

    onInputKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.open();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.moveActive(-1);
          break;
        case 'Enter': {
          const active = this.options()[this.activeIndex];
          if (active) {
            event.preventDefault();
            active.click();
          }
          break;
        }
        case 'Tab':
          this.close();
          break;
        default:
          break;
      }
    }

    options() {
      if (!this.isOpen()) return [];
      return Array.from(this.querySelectorAll(OPTION_SELECTOR)).filter(
        (option) => option.offsetParent !== null
      );
    }

    moveActive(delta) {
      const options = this.options();
      if (!options.length) return;

      let next = this.activeIndex + delta;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;

      this.setActiveIndex(next);
    }

    setActiveIndex(index) {
      const options = this.options();

      options.forEach((option) => {
        option.removeAttribute('data-active');
      });

      this.activeIndex = index;

      const active = options[index];
      this.inputs.forEach((input) => {
        if (active) {
          if (!active.id) active.id = `athena-search-option-${index}`;
          input.setAttribute('aria-activedescendant', active.id);
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      });

      if (active) {
        active.setAttribute('data-active', 'true');
        active.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  customElements.define('athena-search', AthenaSearch);
})();
